import React, { useMemo } from "react";
import TextAvatar from "../../Common/TextAvatar";

const safe = (v, fallback = "N/A") =>
  v === null || v === undefined || String(v).trim() === "" ? fallback : v;

const InfoPill = ({ icon, label, value, tone = "" }) => (
  <div className={`sp-pill ${tone}`}>
    <span className="sp-pill-ic">
      <i className={`fa ${icon}`} />
    </span>
    <div className="sp-pill-tx">
      <div className="sp-pill-lbl">{label}</div>
      <div className="sp-pill-val" title={String(value || "")}>
        {safe(value, "—")}
      </div>
    </div>
  </div>
);

const StudentProfileInfo = ({ profileInfo }) => {
  const p = profileInfo || {};

  const subtitle = useMemo(() => {
    const cls = p.class?.class_name ? ` · ${p.class.class_name}` : "";
    const st = p.status_name ? `${p.status_name}` : "Student";
    return `${st}${cls}`;
  }, [p]);

  const isActive = String(p.status) === "1";

  return (
    <>
      <style>{css}</style>

      <div className="sp-strip">
        {/* left identity */}
        <div className="sp-left">
          <div className="sp-avatar">
            <TextAvatar text={safe(p.initials, "ST")} bgClass={null} size="52px" fontSize="14px" />
          </div>

          <div className="sp-main">
            <div className="sp-kicker">Student profile</div>

            <div className="sp-nameRow">
              <div className="sp-name" title={safe(p.full_name, "Student")}>
                {safe(p.full_name, "Student")}
              </div>

              <span className={`sp-status ${isActive ? "is-active" : "is-inactive"}`}>
                <i className={`fa ${isActive ? "fa-circle-check" : "fa-circle"}`} />
                {isActive ? "Active" : "Inactive"}
              </span>
            </div>

            <div className="sp-sub">{subtitle}</div>
          </div>
        </div>

        {/* right details */}
        <div className="sp-right">
          <InfoPill icon="fa-id-badge" label="Reg No" value={p.reg_no} />
          <InfoPill icon="fa-credit-card" label="Payment code" value={p.payment_code} />
          <InfoPill icon="fa-venus-mars" label="Gender" value={p.gender} />
          <InfoPill icon="fa-home" label="Residence" value={p.residence?.residence_name} />
          <InfoPill icon="fa-phone" label="Home contact" value={p.home_contact} />
          <InfoPill
            icon="fa-users"
            label="Fees group"
            value={p.fees_group?.group_name}
            tone={!p.fees_group?.group_name ? "is-warn" : ""}
          />
        </div>
      </div>
    </>
  );
};

const css = `
  /* -------- strip wrapper -------- */
  .sp-strip{
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: 18px;
    padding: 14px 16px;
    border-radius: var(--yx-card-radius, 18px);
    border: 1px solid var(--yx-border-soft, rgba(15,23,42,.07));
    background: linear-gradient(180deg, rgba(79,70,229,.06), rgba(255,255,255,0));
    box-shadow: var(--yx-card-shadow, 0 1px 2px rgba(16,24,40,.04));
  }

  .sp-left{
    display:flex;
    align-items:center;
    gap: 14px;
    min-width: 280px;
    flex: 0 0 auto;
  }

  /* text avatar styling hook (keeps your TextAvatar but theme colors) */
  .sp-avatar .yx-avatar,
  .sp-avatar .ta-avatar{
    /* if you later add a class in TextAvatar, this will apply */
  }

  .sp-main{ min-width: 0; }

  .sp-kicker{
    font-size: var(--yx-kicker, 12px);
    font-weight: 800;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: rgba(100,116,139,.85);
    margin-bottom: 6px;
  }

  .sp-nameRow{
    display:flex;
    align-items:center;
    gap: 10px;
    min-width: 0;
  }

  .sp-name{
    font-size: 16px;
    font-weight: 950;
    letter-spacing: -0.01em;
    color: var(--yx-text, #1e293b);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 420px;
    line-height: 1.15;
  }

  .sp-sub{
    margin-top: 6px;
    font-size: var(--yx-sub, 12.5px);
    font-weight: 700;
    color: rgba(100,116,139,.92);
    display:flex;
    align-items:center;
    gap: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 520px;
  }

  .sp-status{
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    display:inline-flex;
    align-items:center;
    gap: 8px;
    font-weight: 900;
    font-size: 12px;
    border: 1px solid rgba(79,70,229,.14);
    background: rgba(79,70,229,.08);
    color: var(--yx-accent, #4f46e5);
    flex: 0 0 auto;
  }

  .sp-status.is-inactive{
    border-color: rgba(15,23,42,.10);
    background: rgba(15,23,42,.03);
    color: rgba(71,85,105,.95);
  }

  /* -------- right side pills grid -------- */
  .sp-right{
    display:grid;
    grid-template-columns: repeat(3, minmax(160px, 1fr));
    gap: 10px;
    flex: 1 1 auto;
    min-width: 0;
  }

  .sp-pill{
    display:flex;
    align-items:center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px solid rgba(15,23,42,.06);
    background: rgba(255,255,255,.9);
    min-width: 0;
  }

  .sp-pill-ic{
    width: 34px;
    height: 34px;
    border-radius: 12px;
    display:flex;
    align-items:center;
    justify-content:center;
    background: rgba(79,70,229,.10);
    color: var(--yx-accent, #4f46e5);
    flex: 0 0 auto;
  }

  .sp-pill-tx{ min-width: 0; }

  .sp-pill-lbl{
    font-size: 11px;
    font-weight: 900;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: rgba(100,116,139,.80);
    line-height: 1.1;
  }

  .sp-pill-val{
    margin-top: 4px;
    font-size: 13.5px;
    font-weight: 900;
    color: rgba(30,41,59,.92);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    line-height: 1.15;
  }

  .sp-pill.is-warn{
    border-color: rgba(245,158,11,.18);
    background: rgba(245,158,11,.08);
  }
  .sp-pill.is-warn .sp-pill-ic{
    background: rgba(245,158,11,.14);
    color: rgba(146,64,14,.95);
  }

  /* -------- responsive -------- */
  @media (max-width: 1200px){
    .sp-right{ grid-template-columns: repeat(2, minmax(180px, 1fr)); }
    .sp-name{ max-width: 320px; }
  }

  @media (max-width: 992px){
    .sp-strip{
      flex-direction: column;
      align-items: flex-start;
    }
    .sp-left{ width: 100%; }
    .sp-right{ width: 100%; grid-template-columns: repeat(2, minmax(160px, 1fr)); }
    .sp-name{ max-width: 100%; }
    .sp-sub{ max-width: 100%; }
  }

  @media (max-width: 576px){
    .sp-right{ grid-template-columns: 1fr; }
  }
`;

export default StudentProfileInfo;
